// web/src/components/shell/VaultSwitcher.tsx
//
// Replaces the slimmed-out vault list inside Sidebar after the
// master-password rewrite. Reads `appConfig.vaults` and renders one
// row per vault with the active row highlighted.
//
// Click semantics:
//   - active row: no-op
//   - OPFS-synced row: reuse the master handle directly (no prompt)
//   - S3-synced row: prompt for the master password
//   - any unsynced row: prompt for the vault's own passphrase
//
// After a successful switch the vault's `last_opened_at` is bumped
// and the updated app-config is sealed back to disk via
// `saveAppConfig`. The local masterSession is kept in sync.
//
// Passphrase Uint8Array buffers are zeroized in `finally`. The
// transient string state is cleared right before navigation.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "../../lib/utils";
import { useVault } from "../../hooks/useVault";
import { useSession } from "../../stores/session";
import { useMasterSession } from "../../stores/masterSession";
import { toPassphraseBytes, zeroize } from "../../lib/passphraseBytes";
import type {
  AppConfig,
  S3ConfigFull,
  UrlStyle,
  VaultEntry,
} from "../../worker/api";
import { PassphraseInput } from "../primitives/PassphraseInput";
import { VaultGlyph } from "../primitives/VaultGlyph";

function adapterSubtitle(entry: VaultEntry): string {
  if (entry.adapter.type === "opfs") return "opfs";
  return `${entry.adapter.bucket}/${entry.adapter.prefix}`;
}

function s3ConfigFromEntry(entry: VaultEntry): S3ConfigFull | null {
  if (entry.adapter.type !== "s3-compat") return null;
  const urlStyle: UrlStyle =
    entry.adapter.url_style === "virtual-hosted" ? "virtual-host" : "path";
  return {
    endpoint: entry.adapter.endpoint,
    region: entry.adapter.region,
    bucket: entry.adapter.bucket,
    prefix: entry.adapter.prefix,
    accessKeyId: entry.adapter.access_key_id,
    secretAccessKey: entry.adapter.secret_access_key,
    urlStyle,
  };
}

export function VaultSwitcher() {
  const vault = useVault();
  const navigate = useNavigate();
  const session = useSession((s) => s.session);
  const setSession = useSession((s) => s.setSession);
  const master = useMasterSession((s) => s.master);
  const setAppConfig = useMasterSession((s) => s.setAppConfig);
  const vaults = master?.appConfig?.vaults ?? [];

  const [pendingEntry, setPendingEntry] = useState<VaultEntry | null>(null);
  const [pendingPass, setPendingPass] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);

  function clickRow(entry: VaultEntry): void {
    if (entry.id === session?.vaultId) return;

    if (entry.adapter.type === "opfs" && entry.sync_state === "synced") {
      // Reuse the master handle directly — same OPFS handle was opened
      // at master-unlock time. No re-prompt.
      if (!master) return;
      void completeSwitch(entry, master.masterHandle);
      return;
    }

    setPendingEntry(entry);
    setPendingPass("");
    setUnlockError(null);
  }

  async function completeSwitch(
    entry: VaultEntry,
    handle: number,
  ): Promise<void> {
    if (!master?.appConfig) return;
    const previousHandle = session?.handle ?? null;
    const updatedEntry: VaultEntry = {
      ...entry,
      last_opened_at: Math.floor(Date.now() / 1000),
    };
    const updated: AppConfig = {
      ...master.appConfig,
      vaults: master.appConfig.vaults.map((v) =>
        v.id === entry.id ? updatedEntry : v,
      ),
    };
    // Order: navigate FIRST so the Browser at the OUTGOING vault URL
    // unmounts before we flip session.vaultId. Otherwise its
    // `session.vaultId !== vaultId` redirect-guard re-renders with
    // a session pointing at the new vault while still mounted at the
    // old URL and bounces us to /master. Same gotcha as Sidebar.lockNow.
    navigate(`/v/${entry.id}/~/`);
    setSession({
      handle,
      vaultId: entry.id,
      displayName: entry.display_name,
      unlockedAt: Date.now(),
    });
    try {
      await vault.saveAppConfig(master.masterHandle, updated);
      setAppConfig(updated);
    } catch {
      // Bump is best-effort; switching the session is the user-visible
      // operation and shouldn't fail because of a config write hiccup.
    }
    // Best-effort: lock the previous vault's handle so we never hold
    // keys for more than one vault at a time. Skip when the handle is
    // reused (OPFS-self-switch reuses the master handle — locking it
    // would tear down the master session). Failures are silently
    // swallowed; a leaked handle is non-fatal and shouldn't undo the
    // user-visible switch.
    if (previousHandle !== null && previousHandle !== handle) {
      vault.lockVault(previousHandle).catch(() => {
        /* best-effort */
      });
    }
  }

  async function onSubmitPrompt(): Promise<void> {
    if (!pendingEntry) return;
    setUnlocking(true);
    setUnlockError(null);
    const bytes = toPassphraseBytes(pendingPass);
    try {
      let handle: number;
      if (pendingEntry.adapter.type === "s3-compat") {
        const cfg = s3ConfigFromEntry(pendingEntry);
        if (!cfg) throw new Error("Invalid S3 config");
        handle = await vault.openS3Vault(cfg, bytes);
      } else {
        // opfs unsynced — open via openOpfsVault.
        handle = await vault.openOpfsVault(pendingEntry.adapter.subdir, bytes);
      }
      setPendingPass("");
      setPendingEntry(null);
      await completeSwitch(pendingEntry, handle);
    } catch (err) {
      setUnlockError(err instanceof Error ? err.message : String(err));
    } finally {
      zeroize(bytes);
      setUnlocking(false);
    }
  }

  return (
    <>
      <div className="px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-text-dim">
        Vaults
      </div>
      <nav className="flex flex-col gap-0.5 pb-2 pt-1">
        {vaults.map((v) => {
          const active = v.id === session?.vaultId;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => clickRow(v)}
              aria-current={active ? "true" : undefined}
              className={cn(
                "group flex min-h-12 w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-text-soft md:min-h-0",
                active && "bg-accent-bg text-text",
              )}
            >
              <VaultGlyph
                adapterType={v.adapter.type}
                className="h-4 w-4 text-[8px]"
              />
              <span className="flex flex-1 flex-col truncate">
                <span className="truncate text-text">{v.display_name}</span>
                <span className="truncate text-[10px] text-text-dim">
                  {adapterSubtitle(v)}
                </span>
              </span>
              <span
                className={cn(
                  "font-mono text-[9px]",
                  active ? "text-ok" : "text-text-dim",
                )}
              >
                {active ? "● unlocked" : "locked"}
              </span>
            </button>
          );
        })}

        <Link
          to="/vaults/add/1"
          className="mt-1 flex min-h-12 items-center gap-3 rounded-md border border-dashed border-border px-2 py-1.5 text-[12px] text-text-soft hover:bg-accent-bg/40 hover:text-text md:min-h-0"
        >
          + Add vault
        </Link>
      </nav>

      {pendingEntry && (
        <VaultSwitchPrompt
          entry={pendingEntry}
          passphrase={pendingPass}
          onPassphrase={setPendingPass}
          unlocking={unlocking}
          error={unlockError}
          onCancel={() => {
            setPendingEntry(null);
            setPendingPass("");
            setUnlockError(null);
          }}
          onSubmit={() => void onSubmitPrompt()}
        />
      )}
    </>
  );
}

interface PromptProps {
  readonly entry: VaultEntry;
  readonly passphrase: string;
  readonly onPassphrase: (s: string) => void;
  readonly unlocking: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}

function VaultSwitchPrompt(props: PromptProps) {
  const { entry, passphrase, onPassphrase, unlocking, error, onCancel, onSubmit } =
    props;
  const synced = entry.sync_state === "synced";
  const label = synced ? "Master passphrase" : "Vault passphrase";
  const inputId = synced ? "switch-master-pass" : "switch-vault-pass";

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!unlocking && passphrase.length > 0) onSubmit();
        }}
        className="w-full max-w-sm rounded-lg border border-border bg-bg p-5 shadow-lg"
      >
        <h2 className="mb-3 text-base font-medium tracking-tight">
          Unlock {entry.display_name}
        </h2>
        <label
          htmlFor={inputId}
          className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
        >
          {label}
        </label>
        <PassphraseInput
          id={inputId}
          autoCompleteMode="current-password"
          aria-label={label}
          value={passphrase}
          onChange={(e) => onPassphrase(e.target.value)}
        />
        {error && (
          <div className="mt-2 text-[12px] text-danger">{error}</div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border px-3 py-1.5 text-[12px] text-text-soft"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={unlocking || passphrase.length === 0}
            className="rounded-md bg-accent px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-40"
          >
            {unlocking ? "Unlocking…" : "Unlock"}
          </button>
        </div>
      </form>
    </div>
  );
}
